import { useEffect, useState } from 'react';
import { api, formatGbp } from '../api';
import { usePolling } from '../auth';
import { Badge, Card, Notice, Spinner } from '../components/ui';
import type { SettingsResponse, UniverseEntry } from '../types';

type Toast = (message: string, tone?: 'success' | 'error') => void;

export default function Settings({ pushToast }: { pushToast: Toast }) {
  const settings = usePolling<SettingsResponse>(() => api.settings(), null);
  const universe = usePolling<{ universe: UniverseEntry[] }>(() => api.universe(), null);

  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (universe.data) setDraft(JSON.stringify(universe.data.universe, null, 2));
  }, [universe.data]);

  async function saveUniverse(): Promise<void> {
    setBusy(true);
    try {
      const parsed = JSON.parse(draft) as UniverseEntry[];
      if (!Array.isArray(parsed)) throw new Error('The universe must be a JSON array.');
      await api.saveUniverse(parsed);
      pushToast(`Universe saved with ${parsed.length} instruments.`);
      universe.reload();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Could not save the universe', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function resetUniverse(): Promise<void> {
    setBusy(true);
    try {
      await api.resetUniverse();
      pushToast('Universe restored to the default list.');
      universe.reload();
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Reset failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  if (!settings.data) {
    return (
      <div className="page">
        <Spinner label="Loading settings" />
      </div>
    );
  }

  const config = settings.data;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <p>
            The engine's effective configuration. Costs, risk limits and the daily trade cap come from{' '}
            <code>.env</code> and need a server restart to change. The tradable universe can be edited
            here and takes effect on the next cycle.
          </p>
        </div>
      </div>

      {config.auth.ephemeralSessionSecret && (
        <Notice tone="warn" title="Sessions will not survive a restart">
          <code>JWT_SECRET</code> is missing or shorter than 32 characters, so a random one was
          generated at boot. Set a permanent value with <code>openssl rand -hex 32</code>.
        </Notice>
      )}

      <div className="grid grid-2">
        <Card title="Engine">
          <dl className="kv">
            <dt>Base currency</dt>
            <dd>{config.engine.baseCurrency}</dd>
            <dt>Daily trade cap</dt>
            <dd>{config.engine.maxTradesPerDay} per stream</dd>
            <dt>Cycle interval</dt>
            <dd>{config.engine.cycleIntervalMinutes} minutes</dd>
            <dt>Scheduler</dt>
            <dd>{config.engine.schedulerEnabled ? 'enabled' : 'disabled'}</dd>
            <dt>Default funding</dt>
            <dd>{formatGbp(config.engine.defaultFundingGbp)}</dd>
          </dl>
        </Card>

        <Card title="Simulated dealing costs" subtitle="UK retail defaults">
          <dl className="kv">
            <dt>Commission</dt>
            <dd>{formatGbp(config.costs.commissionGbp)} per trade</dd>
            <dt>FX spread</dt>
            <dd>{config.costs.fxFeePct}% on non-GBP trades</dd>
            <dt>Stamp duty</dt>
            <dd>{config.costs.stampDutyPct}% on UK purchases only</dd>
            <dt>PTM levy</dt>
            <dd>
              {formatGbp(config.costs.ptmLevyGbp)} on UK trades above{' '}
              {formatGbp(config.costs.ptmLevyThresholdGbp)}
            </dd>
            <dt>Slippage</dt>
            <dd>{config.costs.slippageBps} bps against you</dd>
          </dl>
          <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
            These are charged on every simulated fill. Without them, short-horizon strategies look far
            better than they are.
          </p>
        </Card>

        <Card title="Risk limits">
          <dl className="kv">
            <dt>Max position</dt>
            <dd>{config.risk.maxPositionPct}% of equity</dd>
            <dt>Max single order</dt>
            <dd>{config.risk.maxOrderPct}% of equity</dd>
            <dt>Cash reserve</dt>
            <dd>{config.risk.minCashReservePct}% kept uninvested</dd>
            <dt>Drawdown halt</dt>
            <dd>{config.risk.maxDrawdownPct}% from peak</dd>
          </dl>
          <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
            Enforced at execution time, so a model cannot exceed them regardless of what it proposes.
          </p>
        </Card>

        <Card title="Credentials and feeds">
          <dl className="kv">
            <dt>Groq</dt>
            <dd>
              {config.credentials.groq ? (
                <Badge tone="green">configured</Badge>
              ) : (
                <Badge tone="amber">missing</Badge>
              )}
            </dd>
            <dt>Twelve Data</dt>
            <dd>
              {config.credentials.twelveData ? (
                <Badge tone="green">configured</Badge>
              ) : (
                <Badge tone="grey">not set</Badge>
              )}
            </dd>
            <dt>Finnhub</dt>
            <dd>
              {config.credentials.finnhub ? (
                <Badge tone="green">configured</Badge>
              ) : (
                <Badge tone="grey">not set</Badge>
              )}
            </dd>
            <dt>Alpha Vantage</dt>
            <dd>
              {config.credentials.alphaVantage ? (
                <Badge tone="green">configured</Badge>
              ) : (
                <Badge tone="grey">not set</Badge>
              )}
            </dd>
            <dt>Priority</dt>
            <dd>{config.market.providerPriority.join(' → ')}</dd>
            <dt>Default model</dt>
            <dd>{config.groqDefaultModel}</dd>
          </dl>
          <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
            No market-data key is needed: CNBC serves London prices and history, and Nasdaq US
            history. Keys add failover only. Twelve Data's free plan is US-only; London needs its paid
            Grow plan.
          </p>
        </Card>
      </div>

      <Card
        title="Tradable universe"
        subtitle={`${universe.data?.universe.length ?? 0} instruments`}
        actions={
          <>
            <button className="btn" type="button" onClick={() => void resetUniverse()} disabled={busy}>
              Restore defaults
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => void saveUniverse()}
              disabled={busy}
            >
              {busy ? <Spinner /> : 'Save universe'}
            </button>
          </>
        }
      >
        <div className="field">
          <label htmlFor="universe-json">Instruments (JSON)</label>
          <textarea
            id="universe-json"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            spellCheck={false}
            style={{ minHeight: 300 }}
          />
          <span className="field-hint">
            London symbols must end in <code>.L</code> and be marked <code>"exchange": "LSE"</code>;
            everything else is treated as US. Saving clears the quote cache so the next cycle picks up
            the change.
          </span>
        </div>
      </Card>

      <Card title="Authentication">
        <dl className="kv">
          <dt>Username</dt>
          <dd>{config.auth.username}</dd>
          <dt>Session length</dt>
          <dd>{config.auth.sessionTtlHours} hours</dd>
        </dl>
        <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--text-faint)' }}>
          Single-user by design: the account is provisioned from <code>.env</code> at boot and there is
          no signup route. Change the password by editing <code>AUTH_PASSWORD</code> and restarting.
          The server binds to loopback by default; do not expose it to the internet without putting
          HTTPS and additional protection in front of it.
        </p>
      </Card>

      <Notice tone="info" title="Paper trading only">
        {config.note} No broker is connected and no real order is ever placed. Prices are real, fills
        are simulated at the observed price plus slippage and costs, so results should be read as an
        approximation rather than an achievable return.
      </Notice>
    </div>
  );
}
